import throttle from 'lodash/throttle'
import { useState, useMemo, useRef, useEffect } from 'react'
import { Asset } from '@opallabs/cassini'
import type { AssetReference } from '@opallabs/cassini'
import { useSelectObjectById, useListKeyboardNavigation } from '@opallabs/hooks'
import {
  DeprecatedMenuOption,
  MultiSelectType,
  Button,
  SearchInput,
} from '@opallabs/lapidary'
import type { TextInputImperativeHandle } from '@opallabs/lapidary'
import { decorateServiceName } from '@opallabs/wormhole'
import type { ServiceOption } from '../../../../../queries/useSystemServices'
import { styles } from './styles'

export type ServiceItems = Array<ServiceOption>

type CreateContentListProps = {
  focusSearch?: boolean
  onCreateContent: (selectedServices: ServiceItems, byKeypress: boolean) => void
  onCloseList: (byKeypress?: boolean) => void
  services: ServiceItems
}

export const CreateContentList: React.FC<CreateContentListProps> = ({
  focusSearch = false,
  onCreateContent,
  onCloseList,
  services,
}) => {
  const searchRef = useRef<TextInputImperativeHandle>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const onSearch = useMemo(() => throttle(setSearchTerm, 500), [setSearchTerm])

  const filteredServices = useMemo(() => {
    if (!searchTerm) return services
    return services.filter((service) =>
      service.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [services, searchTerm])

  const { selectedItems, onSelect, resetSelections } =
    useSelectObjectById<ServiceOption>({ list: services })
  const {
    highlightedIndex,
    onListKeyDown,
    onListBlur,
    onListFocus,
    listRef,
    setIsFocusableByKeypress,
  } = useListKeyboardNavigation<ServiceOption>({
    list: filteredServices,
    onSelect,
    highlightOnListFocus: true,
  })

  const closeList = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      resetSelections()
      onCloseList(true)
    }
  }

  const isSelected = (id: string) =>
    Boolean(selectedItems.find((service) => service.id === id))

  const onClickAdd = (
    _: React.MouseEvent<HTMLButtonElement>,
    byKeypress: boolean = false
  ) => {
    resetSelections()
    onCreateContent(selectedItems, byKeypress)
  }

  useEffect(() => {
    if (focusSearch) {
      searchRef.current?.focus()
    }
  }, [focusSearch])

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <div css={styles.base} onKeyDown={closeList}>
      <div css={styles.searchWrapper}>
        <SearchInput
          onSearch={onSearch}
          placeholder="Search..."
          ref={searchRef}
          styleOverrides={{ inputContainer: styles.searchInputContainer }}
        />
      </div>
      <div css={styles.listWrapper}>
        <span css={styles.listTitle}>Channels</span>
        <ul
          css={styles.list}
          tabIndex={0}
          ref={listRef}
          role="listbox"
          onFocus={onListFocus}
          onBlur={onListBlur}
          onKeyDown={onListKeyDown}
        >
          {filteredServices.map((service, i) => (
            <DeprecatedMenuOption
              key={service.id}
              id={service.id}
              highlightedByKeyboard={
                highlightedIndex !== null && highlightedIndex === i
              }
              multiSelectType={MultiSelectType.checkbox}
              optionText={service.name}
              onSelect={() => onSelect(service)}
              selected={isSelected(service.id)}
              setIsFocusableByKeypress={setIsFocusableByKeypress}
              styleOverrides={styles.getChannelColor(service.iconName)}
              trailingIcon={{
                glyph: `gem-channel-${decorateServiceName(
                  service.iconName || service.systemName,
                  'star',
                  false,
                  true
                )}-filled`,
                visibility: 'always',
              }}
              trailingNode={
                Boolean(service.assetReference) && (
                  <Asset
                    assetReference={service.assetReference as AssetReference}
                  />
                )
              }
            />
          ))}
          {!filteredServices.length && (
            <li css={styles.emptySearch}> No channels match your search</li>
          )}
        </ul>
        <Button
          variant="primary"
          buttonText="Add"
          disabled={!selectedItems.length}
          className="add-content"
          name="Add content to moment"
          onClick={onClickAdd}
          overrideStyles={styles.addButton}
        />
      </div>
    </div>
  )
  /* eslint-enable jsx-a11y/no-static-element-interactions */
}
